import { useCallback, useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { CockpitLayout, TileDefinition, TileDisplay, TileSize } from "@kundeninfo/api-client";
import { Arbeitsdialog, useBeforeUnload, useBestaetigung } from "@kundeninfo/ui";
import { Abschnitt, Chip, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import type { Objektart } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import type { VerlassenWaechter } from "../../router.js";
import { COCKPIT_TEXT_WEB } from "./cockpitTexts.js";
import { Segments, pairs } from "./Segments.js";
import { applyTileChoice, offeredSizes } from "./tileChoice.js";
import type { TileChoice } from "./tileChoice.js";

/**
 * DIE KACHELWAHL (Spec „Anpassen und Kachelwahl“, 648, 650): je Modul die Kacheln mit „zeigen“, Größe und Darstellung, nur mit erlaubten Werten.
 * Alles läuft auf einem Entwurf; „Speichern“ ist die Bestätigung und gibt die ganze Folge samt neuer Schlüssel weiter. Die Folge selbst ändert sich hier nie:
 * Eine neu gezeigte Kachel steht schon dort, wo sie die normalisierte Folge hat, hinter ihrem Vorgabe-Vorgänger.
 */

export type TilePickerProps = {
  catalog: readonly TileDefinition[]; layout: CockpitLayout; newTiles: readonly string[];
  onSave: (draft: CockpitLayout) => Promise<void>; onClose: () => void;
  registriereVerlassenWaechter?: ((frage: VerlassenWaechter) => () => void) | undefined;
};

const T = COCKPIT_TEXT_WEB.picker;

/** Reihenfolge von `hidden` zählt nicht: Hin- und Zurückschalten ist keine Änderung. */
function sameChoices(a: CockpitLayout, b: CockpitLayout): boolean {
  return a.hidden.length === b.hidden.length && a.hidden.every((key) => b.hidden.includes(key))
    && Object.entries(a.settings).every(([key, { size, display }]) => size === b.settings[key]?.size && display === b.settings[key]?.display);
}

/** Module und Kacheln in Katalogfolge; nur, was der Entwurf kennt: Zeilen und Werte kommen aus derselben Hand. */
function modulesOf(catalog: readonly TileDefinition[], layout: CockpitLayout): TileDefinition[][] {
  const modules = new Map<string, TileDefinition[]>();
  for (const d of catalog) {
    if (Object.hasOwn(layout.settings, d.tileKey)) modules.set(d.module, [...(modules.get(d.module) ?? []), d]);
  }
  return [...modules.values()];
}

function TileRow(p: { definition: TileDefinition; choice: TileChoice; shown: boolean; isNew: boolean; locked: boolean;
  onShown: (shown: boolean) => void; onChoice: (change: Partial<TileChoice>) => void }): ReactElement {
  const { definition: d, choice, locked } = p;
  return (
    <div className="ck__pick">
      <Schalter beschriftung={T.show} aria-label={T.showTile(d.title)} an={p.shown} gesperrt={locked} onGeaendert={(e) => { p.onShown(e.an); }} />
      <div>
        <div className="ck__pick-head">
          <span className="ck__pick-title">{d.title}</span>
          {p.isNew && <Chip statusArt="ok">{T.isNew}</Chip>}
        </div>
        <p className="ck__pick-desc">{d.description}</p>
      </div>
      <div className="ck__pick-choices">
        {/* Die Folge steht in der Texttabelle, für jede Kachel gleich; der Katalog nennt zuerst die Vorgabe. */}
        <span className="ck__pick-choice">
          <span className="ck__pick-label">{T.size}</span>
          <Segments<TileSize> name={T.sizeOf(d.title)} value={choice.size} locked={locked} onChoice={(size) => { p.onChoice({ size }); }}
            options={pairs(T.sizes).filter(([size]) => offeredSizes(d).includes(size))} />
        </span>
        <span className="ck__pick-choice">
          <span className="ck__pick-label">{T.display}</span>
          <Segments<TileDisplay> name={T.displayOf(d.title)} value={choice.display} locked={locked} onChoice={(display) => { p.onChoice({ display }); }}
            options={pairs(T.displays).filter(([display]) => d.displays.includes(display))} />
        </span>
      </div>
    </div>
  );
}

export function TilePicker(p: TilePickerProps): ReactElement {
  const { bestaetigen } = useBestaetigung();
  // Der Stand beim Öffnen: Eine Antwort, die bei offenem Dialog ankommt, ändert weder den Entwurf noch macht sie ihn zu einer Änderung.
  const [initial] = useState(p.layout);
  const [draft, setDraft] = useState(initial);
  const [closedModules, setClosedModules] = useState<readonly string[]>([]);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const setShown = (key: string, shown: boolean): void => {
    setDraft((d) => ({ ...d, hidden: shown ? d.hidden.filter((k) => k !== key) : [...d.hidden, key] }));
  };
  // Immer ein neuer Entwurf: Bleibt die Wahl ohne Wirkung, zieht erst das Neuzeichnen das Segment auf den Wert zurück.
  const setChoice = (d: TileDefinition, change: Partial<TileChoice>): void => {
    setDraft((x) => ({ ...x, settings: { ...x.settings, [d.tileKey]: applyTileChoice(x.settings[d.tileKey]!, change, d) } }));
  };
  const changed = !sameChoices(draft, initial);
  const askDiscard = useCallback(() => bestaetigen({ titel: T.discardQuestion, text: T.discardText,
    bestaetigenText: T.discardConfirm, abbrechenText: T.discardKeep, gefaehrlich: true }), [bestaetigen]);

  // Die Kopfleiste liegt unter dem Schleier, die Zurück-Taste und der Tab nicht (Muster `KundeAnlegen`).
  useBeforeUnload(changed);
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!changed || registriere === undefined) return undefined;
    return registriere(askDiscard);
  }, [changed, registriere, askDiscard]);

  const save = async (): Promise<void> => {
    // Ohne Anlass keine Zeile: Sonst wechselten künftige Kacheln dieses Benutzers von „gleich sichtbar“ zu „ausgeblendet und neu“.
    if (!changed && p.newTiles.length === 0) { p.onClose(); return; }
    setSaving(true);
    setFailed(false);
    try {
      await p.onSave(draft);
      p.onClose();
    } catch {
      setSaving(false);
      setFailed(true);
    }
  };
  const leave = async (): Promise<void> => {
    if (changed && !await askDiscard()) return;
    p.onClose();
  };

  return (
    <Arbeitsdialog titel={T.title} panel="cockpit-tile-picker" onAbbrechen={leave} abbrechenGesperrt={saving}
      meldungen={failed && <Hinweis art="fehler">{T.saveFailed}</Hinweis>}
      fuss={<>
        <KiKnopf art="leise" deaktiviert={saving} onClick={() => { void leave(); }}>{T.cancel}</KiKnopf>
        <KiKnopf art="primaer" deaktiviert={saving} onClick={() => { void save(); }}>{T.save}</KiKnopf>
      </>}>
      <div className="ck__picker">
        {modulesOf(p.catalog, draft).map((tiles) => {
          const { module, moduleTitle, icon } = tiles[0]!;
          return (
            <Abschnitt key={module} titel={moduleTitle} art={icon as Objektart} zaehler={tiles.length} offen={!closedModules.includes(module)}
              onUmgeschaltet={(e) => { setClosedModules((m) => (e.offen ? m.filter((x) => x !== module) : [...m, module])); }}>
              {tiles.map((d) => (
                <TileRow key={d.tileKey} definition={d} choice={draft.settings[d.tileKey]!} shown={!draft.hidden.includes(d.tileKey)}
                  isNew={p.newTiles.includes(d.tileKey)} locked={saving} onShown={(shown) => { setShown(d.tileKey, shown); }} onChoice={(change) => { setChoice(d, change); }} />
              ))}
            </Abschnitt>
          );
        })}
      </div>
    </Arbeitsdialog>
  );
}
